import type { TFunction } from 'i18next';

import type { ActivityLogEntry, Task } from './types';

const TASK_NS = 'translation';
const ALERT_TASK_PREFIX = 'operationalDashboard.alertTask';
const ACTIVITY_LOG_DESCRIPTION_PREFIX = 'operationalDashboard.activityLogDescription';

/**
 * Renders the user-facing title for a task. Every task is alert-derived, so
 * we always translate via i18next using `alertCode` + `templateVariables`.
 *
 * Returns an empty string defensively if `alertCode` is missing — that would
 * indicate a data corruption bug, not a normal state.
 */
export function renderTaskTitle(task: Task, t: TFunction): string {
    if (!task.alertCode) return '';
    return t(`${ALERT_TASK_PREFIX}.${task.alertCode}.title`, {
        ns: TASK_NS,
        ...task.templateVariables,
    });
}

/**
 * Renders the user-facing description for a task. Same contract as
 * `renderTaskTitle`: always translated via the alert i18n namespace.
 */
export function renderTaskDescription(task: Task, t: TFunction): string {
    if (!task.alertCode) return '';
    return t(`${ALERT_TASK_PREFIX}.${task.alertCode}.description`, {
        ns: TASK_NS,
        ...task.templateVariables,
    });
}

/**
 * Returns a locale-specific documentation URL for a task's alert code, or an
 * empty string if no guide is configured for that alert. The URL is defined
 * as an optional `guideUrl` key in each locale's i18n bundle under
 * `operationalDashboard.alertTask.<alertCode>.guideUrl`.
 */
export function renderTaskGuideUrl(task: Task, t: TFunction): string {
    if (!task.alertCode) return '';
    const key = `${ALERT_TASK_PREFIX}.${task.alertCode}.guideUrl`;
    const resolved = t(key, { ns: TASK_NS, defaultValue: '' });
    return resolved === key ? '' : resolved;
}

/**
 * Renders the title for an activity-log entry. Reuses task-level rendering for
 * alert-derived events (so the activity log mirrors the task UI), and falls
 * back to the entry's own free-form title for manual events.
 */
export function renderActivityLogTitle(entry: ActivityLogEntry, t: TFunction): string {
    if (entry.title !== null && entry.title !== undefined) {
        return entry.title;
    }
    if (entry.alertCode) {
        return t(`${ALERT_TASK_PREFIX}.${entry.alertCode}.title`, {
            ns: TASK_NS,
            ...(entry.templateVariables ?? {}),
        });
    }
    return '';
}

/**
 * Renders the description for an activity-log entry. Operator comments are
 * returned verbatim; system-generated text is translated via `descriptionKey`
 * with the snapshotted template variables.
 */
export function renderActivityLogDescription(entry: ActivityLogEntry, t: TFunction): string {
    if (entry.description !== null && entry.description !== undefined && entry.description !== '') {
        return entry.description;
    }
    if (entry.descriptionKey) {
        return t(`${ACTIVITY_LOG_DESCRIPTION_PREFIX}.${entry.descriptionKey}`, {
            ns: TASK_NS,
            ...(entry.templateVariables ?? {}),
        });
    }
    return '';
}
